import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Switch } from "@headlessui/react";
import { toast } from "react-toastify";
import {
  BsArrowsFullscreen,
  BsBoxArrowUpRight,
  BsCameraVideoFill,
  BsCameraVideoOffFill,
  BsChevronDown,
  BsFullscreenExit,
  BsLayoutSplit,
  BsLink45Deg,
  BsMicFill,
  BsMicMuteFill,
  BsPauseFill,
  BsPlayFill,
  BsShareFill,
  BsStopFill,
  BsVolumeMuteFill,
  BsVolumeUpFill,
} from "react-icons/bs";
import { FaExclamationTriangle, FaHeart } from "react-icons/fa";
import { TbWorld } from "react-icons/tb";
import Leftsidebar from "./sidebar/leftsidebar";
import LeftResponsivesidebar from "./sidebar/left-responsive";
import Headmenu from "./sidebar/headmenu";
import golivecam from "../assets/images/icons/golive-cam.png";
import shareIcon from "../assets/images/icons/shareIcon.svg";
import editgolive from "../assets/images/icons/edit-golive.svg";
import greenGlobal from "../assets/images/icons/green-global.png";
import profileimg from "../assets/profile.png";
import logo from "../assets/logo/Logo.svg";
import closeIcon from "../assets/images/icons/close-circle.png";
import GoLiveDonationgiftModal from "./golivedonategiftModal";
import GoLiveDonationModal from "./golivedonationmodal";
import DonationHistory from "./DonationHistory";
import ReportStreamModal from "./ReportStreamModal";
import useLiveBroadcast from "../hook/useLiveBroadcast";
import useLiveViewer from "../hook/useLiveViewer";
import useLiveStage, { SPLIT_LAYOUTS, stageLayout } from "../hook/useLiveStage";
import { saveMetadata } from "./services/liveService";
import {
  goLiveBlockReason,
  PlanLimitsBar,
  PRIVATE_LIVE_UPGRADE_REASON,
  UpgradePlanModal,
  useSubscriptionStatus,
} from "./subscription/PlanPrompts";
import { fetchOwnProfile, getStoredToken, getUser } from "../utils/auth";
import {
  authHeaders,
  formatPrice,
  InfoTip,
  LiveCommentsPanel,
  LiveSummary,
  LiveTimeLimitWarning,
  PendingLiveBanner,
  useLiveComments,
  useNow,
} from "./becomesell/LiveShoppingParts";
import {
  DEFAULT_LIVE_SETTINGS,
  GiftingControls,
  GuestPrivacy,
  GuestRequests,
  HOST_PERMISSIONS,
  ModeratorsSection,
  MultiHostSection,
  PrivacyControls,
  useCommentModeration,
  useGuestSeat,
  useLiveLikes,
  useLiveStudioSetup,
} from "./golivestreamsetup";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const BANK_FIELDS = ["bankName", "ifsc", "beneficiaryName", "accountNumber"];

const formatDateTime = (value) => new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

const formatElapsed = (startedAt, now) => {
  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  return [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

const personName = (person) => [person?.first_name, person?.last_name].filter(Boolean).join(" ") || person?.username || "User";

const toDonationEvent = (donation) => ({
  id: `donation-${donation.id ?? `${donation.donor?.id}-${donation.timestamp}`}`,
  name: personName(donation.donor),
  username: donation.donor?.username,
  amount: Number(donation.amount) || 0,
  message: donation.message || "Sent a donation",
  timestamp: donation.timestamp || donation.created_at || new Date().toISOString(),
});

// Donations and gifts sent during the live, shown in the chat and the host's history.
// With `historyStreamId` (host only) donations made before the page was opened are loaded too; gifts have no history API.
const useDonationEvents = (socket, historyStreamId = null) => {
  const [events, setEvents] = useState([]);

  const addEvents = useCallback(
    (list) =>
      setEvents((prev) =>
        [...prev, ...list.filter((event) => !prev.some((e) => e.id === event.id))].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        )
      ),
    []
  );

  useEffect(() => {
    // Each live starts with its own history
    setEvents([]);
    if (!historyStreamId) return;
    axios
      .get(`${API_BASE_URL}/live/${historyStreamId}/donation/summary`, { headers: authHeaders() })
      .then((res) => addEvents((res.data.result?.donations || []).map(toDonationEvent)))
      .catch(() => {});
  }, [historyStreamId, addEvents]);

  useEffect(() => {
    if (!socket) return;
    const onDonation = ({ donation }) => addEvents([toDonationEvent(donation)]);
    const onGift = (data) =>
      addEvents([
        {
          id: `gift-${data.userId}-${Date.now()}`,
          name: data.username || "User",
          username: data.username,
          amount: Number(data.coinValue) || 0,
          message: `Sent ${data.gift?.emoji || "🎁"} ${data.gift?.name || "a gift"}`,
          timestamp: new Date().toISOString(),
        },
      ]);

    socket.on("donation-received", onDonation);
    socket.on("gift-sent", onGift);
    return () => {
      socket.off("donation-received", onDonation);
      socket.off("gift-sent", onGift);
    };
  }, [socket, addEvents]);

  return events;
};

const withDonationComments = (comments, events) =>
  [
    ...comments,
    ...events.map((e) => ({
      commentId: e.id,
      userFullName: e.name,
      username: e.username,
      comment: e.message,
      timestamp: e.timestamp,
      amount: e.amount,
    })),
  ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

const copyLiveLink = async (streamId) => {
  try {
    await navigator.clipboard.writeText(`${window.location.origin}/stream/${streamId}`);
    toast.success("Live link copied");
  } catch (error) {
    toast.error("Unable to copy the live link");
  }
};

const shareLiveLink = async (streamId) => {
  if (!navigator.share) return copyLiveLink(streamId);
  try {
    await navigator.share({ url: `${window.location.origin}/stream/${streamId}` });
  } catch (error) {
    if (error.name !== "AbortError") toast.error("Unable to share the live");
  }
};

const PageLayout = ({ children }) => (
  <div className="dashboard w-full min-h-screen flex">
    <Leftsidebar />
    <main className="flex-1 min-w-0 p-4 md:p-6">
      <LeftResponsivesidebar />
      <Headmenu />
      {children}
    </main>
  </div>
);

const LiveLinkButtons = ({ streamId, className = "" }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <button onClick={() => copyLiveLink(streamId)} title="Copy live link" className="rounded-full bg-[#095228] p-2 text-white">
      <BsLink45Deg />
    </button>
    <button onClick={() => shareLiveLink(streamId)} title="Share live" className="rounded-full bg-[#095228] p-2 text-white">
      <BsShareFill />
    </button>
  </div>
);

const LiveHeader = ({ title, startedAt, visibility, now, actions }) => (
  <div className="mt-6 flex items-end justify-between gap-4">
    <div className="min-w-0">
      <h1 className="truncate text-lg font-semibold">{title}</h1>
      <p className="flex items-center gap-1 text-[10px] text-gray-500">
        {startedAt && formatDateTime(startedAt)}
        <span className="flex items-center gap-1 text-[#095228]">
          | <img src={greenGlobal} alt="" className="h-3 w-3" />
          {visibility === "private" ? "Private" : "Public"}
        </span>
      </p>
    </div>
    <div className="flex items-center gap-4">
      {startedAt && <span className="text-2xl font-semibold tabular-nums">{formatElapsed(startedAt, now)}</span>}
      {actions}
    </div>
  </div>
);

const NameBadge = ({ name, picture, date }) => (
  <div className="absolute left-0 top-0 z-10 flex items-center gap-2 rounded-br-2xl bg-white/60 px-4 py-2 text-black backdrop-blur-sm">
    <img src={picture || profileimg} alt="" className="h-8 w-8 rounded-full bg-white object-cover" />
    <div>
      <p className="text-sm font-semibold">{name}</p>
      {date && <p className="text-[10px]">{formatDateTime(date)}</p>}
    </div>
  </div>
);

const InfoField = ({ label, children }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <div className="mt-1 text-sm">{children}</div>
  </div>
);

const SectionTitle = ({ title, onEdit }) => (
  <div className="flex items-center justify-between border-b pb-2">
    <h2 className="font-semibold text-[#095228]">{title}</h2>
    {onEdit && (
      <button onClick={onEdit} title={`Edit ${title}`}>
        <img src={editgolive} alt="Edit" className="h-4 w-4" />
      </button>
    )}
  </div>
);

const FundInformation = ({ fund, onEdit }) => (
  <section className="space-y-4">
    <SectionTitle title="Fund Information" onEdit={onEdit} />
    <InfoField label="Title">
      <span className="text-lg">{fund.title}</span>
    </InfoField>
    {fund.description && (
      <InfoField label="Description">
        <p className="whitespace-pre-line break-words text-xs">{fund.description}</p>
      </InfoField>
    )}
    <div className="flex flex-wrap gap-10">
      {fund.category && <InfoField label="Category">{fund.category}</InfoField>}
      {Number(fund.goalAmount) > 0 && (
        <InfoField label="Goal Amount">
          <span className="text-lg font-semibold text-[#BE1F2E]">{formatPrice(fund.goalAmount)}</span>
        </InfoField>
      )}
    </div>
  </section>
);

const BankingInformation = ({ bank, onEdit }) => (
  <section className="space-y-4">
    <SectionTitle title="Banking Information" onEdit={onEdit} />
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <InfoField label="Beneficiary Name">{bank.beneficiaryName || "-"}</InfoField>
      <InfoField label="Bank Name">{bank.bankName || "-"}</InfoField>
      <InfoField label="Account Number">{bank.accountNumber || "-"}</InfoField>
      <InfoField label="SWIFT Code">{bank.ifsc || "-"}</InfoField>
    </div>
  </section>
);

const ToggleButton = ({ on, onClick, title, onIcon, offIcon }) => (
  <button onClick={onClick} title={title} className="rounded-full bg-white p-3 text-gray-600">
    {on ? onIcon : offIcon}
  </button>
);

const CommentsSwitch = ({ checked, onChange }) => (
  <div className="flex items-center gap-2">
    <Switch
      checked={checked}
      onChange={onChange}
      className={`${checked ? "bg-[#095228]" : "bg-gray-400"} relative inline-flex h-6 w-11 items-center rounded-full`}
    >
      <span className={`${checked ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition`} />
    </Switch>
    <span className="text-sm">Comments</span>
  </div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-full px-5 py-2 text-sm font-semibold ${active ? "bg-[#095228] text-white" : "bg-white text-gray-600"}`}
  >
    {children}
  </button>
);

const StageVideo = ({ stream, muted = false, mirrored = false }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <video ref={ref} autoPlay playsInline muted={muted} className={`h-full w-full object-cover ${mirrored ? "-scale-x-100" : ""}`} />
  );
};

const PausedNotice = ({ subtitle }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#222222] px-4 text-center">
    <p className="text-3xl font-semibold tracking-wide sm:text-5xl">Live Paused.</p>
    {subtitle && <p className="mt-2 text-sm">{subtitle}</p>}
  </div>
);

// Tiles arranged like the broadcast (layoutSlots in useLiveBroadcast); the first tile is the host
const StageTiles = ({ layout, tiles }) => {
  const renderTile = (tile, className) => (
    <div key={tile.key} className={`relative min-h-0 min-w-0 overflow-hidden rounded-xl bg-black ${className}`}>
      {tile.content}
      {layout !== "single" && (
        <span className="absolute left-3 top-3 max-w-[60%] truncate rounded-full bg-black/60 px-3 py-1 text-xs">{tile.name}</span>
      )}
      {tile.action && <div className="absolute right-3 top-3">{tile.action}</div>}
    </div>
  );
  if (layout === "pip") {
    const [host, ...guests] = tiles;
    return (
      <div className="relative h-full w-full">
        {renderTile(host, "h-full w-full rounded-none")}
        <div className="absolute bottom-3 right-3 flex gap-2">
          {guests.map((tile) => renderTile(tile, "aspect-video w-32 border-4 border-[#F5E50A] sm:w-52"))}
        </div>
      </div>
    );
  }
  return (
    <div className={`flex h-full w-full gap-2 ${layout === "horizontal_split" ? "flex-col" : ""}`}>
      {tiles.map((tile) => renderTile(tile, "flex-1"))}
    </div>
  );
};

const FloatingPanel = ({ title, className = "", children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`absolute bottom-0 z-10 w-56 rounded-t-xl bg-white/85 text-black backdrop-blur-sm sm:w-72 ${className}`}>
      <button onClick={() => setOpen((value) => !value)} className="flex w-full items-center justify-between px-3 py-2 text-sm font-semibold">
        {title}
        <BsChevronDown className={`rounded-full bg-[#095228]/30 p-0.5 text-lg transition ${open ? "" : "rotate-180"}`} />
      </button>
      {open && <div className="max-h-72 overflow-y-auto px-2 pb-2">{children}</div>}
    </div>
  );
};

const RemoveFromStageButton = ({ onClick, title }) => (
  <button onClick={onClick} title={title} className="rounded-full bg-black/50 p-2 text-white">
    <BsBoxArrowUpRight />
  </button>
);

const HostStudio = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [fund, setFund] = useState(state);
  const [titleDraft, setTitleDraft] = useState(state?.title || "");
  const [categories, setCategories] = useState([]);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("setup");
  const [theater, setTheater] = useState(false);
  const [mutedGuestIds, setMutedGuestIds] = useState([]);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const { status: planStatus, refresh: refreshPlanStatus } = useSubscriptionStatus();

  const {
    cameraStream,
    cameraError,
    startCamera,
    live,
    viewerCount,
    socket,
    goLive,
    stopLive,
    composeStage,
    pendingLive,
    resumeLive,
    discardLive,
    endedLive,
    sessionLimit,
  } = useLiveBroadcast("donation");
  const streamId = live?.streamId;
  const now = useNow();
  const { comments, sendComment } = useLiveComments(streamId, socket);
  const donationEvents = useDonationEvents(socket, streamId);
  const setup = useLiveStudioSetup(streamId, socket);
  const { likes, like } = useLiveLikes(streamId, socket);
  const user = getUser();
  const getCommentActions = useCommentModeration(streamId, HOST_PERMISSIONS, user?.id);

  // The host joins the stage once someone is approved; removed guests are left out even if still connected
  const approvedGuests = [...setup.cohosts, ...setup.guests].filter((guest) => guest.status === "approved");
  const approvedKey = approvedGuests.map((guest) => guest.user_id).join(",");
  const stage = useLiveStage(streamId, cameraStream, !!live && setup.settings.guests_enabled && approvedGuests.length > 0);
  const stageGuests = useMemo(() => {
    const ids = approvedKey.split(",");
    return stage.participants.filter((participant) => ids.includes(participant.userId));
  }, [stage.participants, approvedKey]);
  const layout = stageLayout(setup.settings.layout, stageGuests.length);
  // Muting only takes a guest out of the broadcast; the host and other guests still hear them on the stage
  const broadcastGuests = useMemo(
    () => stageGuests.map((guest) => ({ ...guest, muted: mutedGuestIds.includes(guest.userId) })),
    [stageGuests, mutedGuestIds]
  );

  useEffect(() => {
    if (live) composeStage(layout, broadcastGuests, paused);
  }, [live, layout, broadcastGuests, paused, composeStage]);

  useEffect(() => {
    if (stage.error) toast.error(stage.error);
  }, [stage.error]);

  useEffect(() => {
    if (!theater) return;
    const onKeyDown = (e) => e.key === "Escape" && setTheater(false);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [theater]);

  // The fund details come from the Go Live form on the donation page
  useEffect(() => {
    if (!state) navigate("/donation", { replace: true });
  }, [state, navigate]);

  useEffect(() => {
    axios
      .post(`${API_BASE_URL}/post/get-categorylist`, { filters: {}, page: 1, limit: 100 }, { headers: authHeaders() })
      .then((res) => setCategories(res.data.result?.categories || []))
      .catch(() => setCategories([]));
  }, []);

  // Nothing is sent to viewers while the live is paused
  useEffect(() => {
    cameraStream?.getAudioTracks().forEach((track) => (track.enabled = micOn && !paused));
    cameraStream?.getVideoTracks().forEach((track) => (track.enabled = camOn && !paused));
  }, [cameraStream, micOn, camOn, paused]);

  useEffect(() => {
    setTitleDraft(fund?.title || "");
  }, [fund?.title]);

  // Stop, End Live and the server ending the live (auto-end, admin) all land here
  useEffect(() => {
    if (!endedLive) return;
    setPaused(false);
    setTheater(false);
    setTab("setup");
    refreshPlanStatus();
    if (endedLive.byServer) toast.info("Your live has ended");
  }, [endedLive, refreshPlanStatus]);

  if (!fund) return null;

  const handleGoLive = async () => {
    if (!fund.title?.trim()) {
      toast.error("Enter a stream title");
      return;
    }
    const blockReason = goLiveBlockReason(planStatus, {
      donations: true,
      privateAudience: setup.settings.view_audience !== "public",
    });
    if (blockReason) {
      setUpgradeReason(blockReason);
      return;
    }
    setBusy(true);
    try {
      try {
        await saveBankDetails(fund);
      } catch (error) {
        throw new Error(error.response?.data?.message || error.message || "Unable to save the bank details");
      }
      await goLive({
        init: { stream_type: "donation", navigation_source: "donate" },
        configuration: {
          ...setup.settings,
          // "Private" here is the follower/friend audience, not the API's premium-only visibility
          visibility: "public",
          chat_enabled: chatEnabled,
          donation_enabled: true,
          reactions_enabled: true,
        },
        metadata: {
          title: fund.title.trim(),
          category: fund.category,
          tags: [],
          description: fund.description || "",
          goal_amount: Number(fund.goalAmount),
        },
      });
    } catch (error) {
      console.error("Go Live error:", error);
      toast.error(error.message || "Unable to start the live");
    } finally {
      setBusy(false);
    }
  };

  const handleStopLive = async () => {
    setBusy(true);
    try {
      await stopLive();
      toast.success("Your live has ended");
    } catch (error) {
      toast.error(error.message || "Unable to end the live");
    } finally {
      setBusy(false);
    }
  };

  const handleResumeLive = async () => {
    setBusy(true);
    try {
      const resumed = await resumeLive();
      setFund({
        ...fund,
        title: resumed.title || fund.title,
        description: resumed.description ?? fund.description,
        goalAmount: resumed.goal_amount ?? fund.goalAmount,
      });
      setChatEnabled(resumed.chat_enabled);
      setPaused(resumed.status === "paused");
    } catch (error) {
      toast.error(error.message || "Unable to resume the live");
    } finally {
      setBusy(false);
    }
  };

  const handleDiscardLive = async () => {
    setBusy(true);
    try {
      await discardLive();
      toast.success("Your live has ended");
    } catch (error) {
      toast.error(error.message || "Unable to end the live");
    } finally {
      setBusy(false);
    }
  };

  const handleTogglePause = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE_URL}/live/${streamId}/${paused ? "resume" : "pause"}`, {}, { headers: authHeaders() });
      setPaused((value) => !value);
    } catch (error) {
      toast.error(error.response?.data?.message || `Unable to ${paused ? "resume" : "pause"} the live`);
    } finally {
      setBusy(false);
    }
  };

  const handleToggleComments = async (enabled) => {
    if (!streamId) {
      setChatEnabled(enabled);
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/live/${streamId}/toggle-comments`, { chat_enabled: enabled }, { headers: authHeaders() });
      setChatEnabled(enabled);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update comments");
    }
  };

  // Each save adds an organizer row and viewers see the newest one, so nothing is saved before the live starts
  const saveBankDetails = async (form) => {
    const profileRes = await fetchOwnProfile(getStoredToken());
    const owner = (profileRes.ok && (await profileRes.json()).result) || user;
    const res = await axios.post(
      `${API_BASE_URL}/user/add-organizer-details`,
      {
        organization_name: owner?.username || "public",
        organization_address: owner?.location || "public",
        bank_name: form.bankName,
        bank_account_number: form.accountNumber,
        bank_ifsc_code: form.ifsc,
        bank_account_holder_name: form.beneficiaryName,
        bank_account_holder_address: owner?.location || "public",
      },
      { headers: authHeaders() }
    );
    if (!res.data.success) throw new Error(res.data.message);
  };

  const handleFundSave = async (form) => {
    if (!streamId) {
      setFund(form);
      return true;
    }
    try {
      if (BANK_FIELDS.some((field) => form[field] !== fund[field])) await saveBankDetails(form);
      await saveMetadata({
        stream_id: streamId,
        title: form.title.trim(),
        category: form.category,
        description: form.description || "",
        goal_amount: Number(form.goalAmount),
      });
      setFund(form);
      toast.success("Details updated");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Unable to update the details");
      return false;
    }
  };

  const handleCancel = () => {
    const hasSetup =
      setup.moderators.length > 0 ||
      setup.cohosts.length > 0 ||
      Object.keys(DEFAULT_LIVE_SETTINGS).some((key) => setup.settings[key] !== DEFAULT_LIVE_SETTINGS[key]);
    if (hasSetup && !window.confirm("Discard this live setup?")) return;
    navigate("/donation", { replace: true });
  };

  const handleTitleBlur = async () => {
    const title = titleDraft.trim();
    if (!title) {
      toast.error("Enter a stream title");
      setTitleDraft(fund.title);
      return;
    }
    if (title === fund.title) return;
    if (!(await handleFundSave({ ...fund, title }))) setTitleDraft(fund.title);
  };

  const history = donationEvents.map((e) => ({
    id: e.id,
    name: e.name,
    time: new Date(e.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
    amount: e.amount,
  }));
  const historyTotal = donationEvents.reduce((sum, e) => sum + e.amount, 0);
  const moderatorIds = [user?.id, ...setup.moderators.map((m) => m.user.id)];
  const isPrivate = setup.settings.view_audience !== "public";
  const visibilitySelect = (
    <div className="flex items-center gap-1">
      <TbWorld className="text-lg" />
      <select
        value={isPrivate ? "private" : "public"}
        onChange={(e) =>
          e.target.value === "private" && planStatus && !planStatus.private_live_enabled
            ? setUpgradeReason(PRIVATE_LIVE_UPGRADE_REASON)
            : setup.updateSettings({ view_audience: e.target.value === "private" ? "followers" : "public" })
        }
        className="bg-transparent text-sm focus:outline-none"
      >
        <option value="public">Public</option>
        <option value="private">Private</option>
      </select>
      <InfoTip
        label="About live visibility"
        text={
          isPrivate
            ? `Only your ${setup.settings.view_audience} can watch. Choose Followers or Friends in Privacy Controls.`
            : "Everyone can watch this live."
        }
      />
    </div>
  );
  const onStage = broadcastGuests.map((guest) => {
    const seat = approvedGuests.find((g) => String(g.user_id) === guest.userId);
    return { ...guest, name: seat?.user ? personName(seat.user) : guest.username, picture: seat?.user?.profile_picture_url };
  });
  const hostIds = [user?.id, ...setup.cohosts.map((g) => g.user_id)];

  return (
    <PageLayout>
      <LiveHeader
        title={fund.title}
        startedAt={live?.startedAt}
        visibility={isPrivate ? "private" : "public"}
        now={now}
        actions={live && <LiveLinkButtons streamId={streamId} />}
      />
      {live && <LiveTimeLimitWarning sessionLimit={sessionLimit} now={now} />}
      {pendingLive && !live && (
        <PendingLiveBanner
          title={pendingLive.title}
          busy={busy}
          canResume={!!cameraStream}
          onResume={handleResumeLive}
          onEnd={handleDiscardLive}
        />
      )}
      {!live && (
        <div className="mt-3 max-w-xl">
          <PlanLimitsBar status={planStatus} />
        </div>
      )}

      <div className="mt-4 flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          <div className={theater ? "fixed inset-0 z-50 flex flex-col bg-white p-4 md:p-8" : ""}>
            {/* The requests column is hidden behind the full-screen view */}
            {theater && setup.requests.length > 0 && (
              <div className="absolute left-1/2 top-6 z-20 w-80 max-w-[90vw] -translate-x-1/2 rounded-xl shadow-xl">
                <GuestRequests requests={setup.requests} guests={[]} onReview={setup.reviewRequest} onRemove={setup.removeGuest} />
              </div>
            )}
            <div className={`rounded-xl bg-[#EBF8ED] ${theater ? "flex min-h-0 flex-1 flex-col" : ""}`}>
              <div
                className={`relative overflow-hidden rounded-xl bg-black text-white ${
                  theater ? "min-h-0 flex-1" : "h-[260px] sm:h-[400px]"
                }`}
              >
                {layout === "single" && (
                  <NameBadge name={personName(user)} picture={user?.profile_picture_url} date={live?.startedAt || new Date()} />
                )}
                {cameraStream ? (
                  <StageTiles
                    layout={layout}
                    tiles={[
                      {
                        key: "host",
                        name: personName(user),
                        content: (
                          <>
                            {/* Mirrored like a front camera for the host only; viewers receive the unmirrored broadcast */}
                            <StageVideo stream={cameraStream} muted mirrored />
                            {paused && <PausedNotice />}
                          </>
                        ),
                      },
                      ...onStage.map((guest) => ({
                        key: guest.id,
                        name: guest.name,
                        content: (
                          <>
                            <StageVideo stream={guest.mediaStream} />
                            {guest.muted && (
                              <span title="Muted for viewers" className="absolute bottom-3 left-3 rounded-full bg-black/60 p-2">
                                <BsMicMuteFill />
                              </span>
                            )}
                          </>
                        ),
                        action: (
                          <RemoveFromStageButton
                            onClick={() => setup.removeGuest(Number(guest.userId))}
                            title="Remove from stage"
                          />
                        ),
                      })),
                    ]}
                  />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                    <img src={golivecam} alt="" className="mb-3" />
                    <p className="text-lg font-semibold">{cameraError ? "Allow Access To Camera" : "Starting camera..."}</p>
                    {cameraError && (
                      <>
                        <p className="mt-1 max-w-md text-xs text-gray-300">
                          Your browser is not allowing Live Producer access to your camera. Go to your browser settings and
                          allow camera permission.
                        </p>
                        <button onClick={startCamera} className="mt-5 rounded-lg bg-[#D9D9D9] px-8 py-2 font-semibold text-black">
                          Retry
                        </button>
                      </>
                    )}
                  </div>
                )}
                {live && (
                  <>
                    {/* Leaves room for the remove button of the right-hand guest tile */}
                    <div
                      className={`absolute top-3 z-10 flex items-center gap-2 ${layout === "vertical_split" ? "right-16" : "right-3"}`}
                    >
                      <span className="rounded-full bg-black/50 px-3 py-1 text-xs font-semibold">{viewerCount} Viewers</span>
                      <span className="rounded-full bg-[#BE1F2E] px-4 py-1 text-xs font-semibold">{paused ? "PAUSED" : "LIVE"}</span>
                      <button
                        onClick={() => setTheater((value) => !value)}
                        title={theater ? "Exit full screen" : "Full screen"}
                        className="rounded-full bg-black/50 p-2"
                      >
                        {theater ? <BsFullscreenExit /> : <BsArrowsFullscreen />}
                      </button>
                    </div>
                    {stageGuests.length > 0 && (
                      <button
                        onClick={() =>
                          setup.updateSettings({
                            layout: SPLIT_LAYOUTS[(SPLIT_LAYOUTS.indexOf(layout) + 1) % SPLIT_LAYOUTS.length],
                          })
                        }
                        title="Change video layout"
                        className="absolute bottom-14 left-4 z-10 flex flex-col items-center gap-2"
                      >
                        <span className="rotate-180 text-xs font-semibold [writing-mode:vertical-rl]">Video Layout</span>
                        <span className="rounded-full bg-black/50 p-2">
                          <BsLayoutSplit />
                        </span>
                      </button>
                    )}
                    {onStage.length > 0 && (
                      <FloatingPanel title="On Stage" className="left-4">
                        {[
                          {
                            key: "host",
                            name: personName(user),
                            picture: user?.profile_picture_url,
                            muted: !micOn,
                            onToggle: () => setMicOn((value) => !value),
                          },
                          ...onStage.map((guest) => ({
                            key: guest.id,
                            name: guest.name,
                            picture: guest.picture,
                            muted: guest.muted,
                            onToggle: () =>
                              setMutedGuestIds((ids) =>
                                ids.includes(guest.userId) ? ids.filter((id) => id !== guest.userId) : [...ids, guest.userId]
                              ),
                          })),
                        ].map((person) => (
                          <div key={person.key} className="mb-1 flex items-center gap-2 rounded-lg bg-white p-2">
                            <img src={person.picture || profileimg} alt="" className="h-7 w-7 rounded-full object-cover" />
                            <span className="truncate text-sm font-semibold">{person.name}</span>
                            <button
                              onClick={person.onToggle}
                              title={person.muted ? "Unmute for viewers" : "Mute for viewers"}
                              className="ml-auto rounded-full bg-[#095228]/30 p-1.5 text-[#095228]"
                            >
                              {person.muted ? <BsMicMuteFill /> : <BsMicFill />}
                            </button>
                          </div>
                        ))}
                      </FloatingPanel>
                    )}
                    {theater && (
                      <FloatingPanel title="Chat History" className="right-4">
                        <LiveCommentsPanel
                          comments={withDonationComments(comments, donationEvents)}
                          chatEnabled={chatEnabled}
                          onSend={sendComment}
                          getActions={getCommentActions}
                          className="h-72"
                        />
                      </FloatingPanel>
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-3">
                  <ToggleButton
                    on={micOn}
                    onClick={() => setMicOn((value) => !value)}
                    title={micOn ? "Mute microphone" : "Unmute microphone"}
                    onIcon={<BsMicFill />}
                    offIcon={<BsMicMuteFill />}
                  />
                  <ToggleButton
                    on={camOn}
                    onClick={() => setCamOn((value) => !value)}
                    title={camOn ? "Turn camera off" : "Turn camera on"}
                    onIcon={<BsCameraVideoFill />}
                    offIcon={<BsCameraVideoOffFill />}
                  />
                  <CommentsSwitch checked={chatEnabled} onChange={handleToggleComments} />
                  {theater && live && (
                    <span className="border-l-2 border-[#095228] pl-3 text-2xl font-semibold tabular-nums">
                      {formatElapsed(live.startedAt, now)}
                    </span>
                  )}
                </div>
                {live ? (
                  <div className="flex items-center gap-3">
                    {visibilitySelect}
                    <button
                      onClick={handleTogglePause}
                      disabled={busy}
                      className="rounded-full border border-[#095228] bg-white px-8 py-2 font-semibold text-[#095228] disabled:opacity-60"
                    >
                      <span className="flex items-center gap-2">
                        {paused ? <BsPlayFill /> : <BsPauseFill />}
                        {paused ? "Resume" : "Pause"}
                      </span>
                    </button>
                    <button
                      onClick={handleStopLive}
                      disabled={busy}
                      className="flex items-center gap-2 rounded-full border border-[#BE1F2E] bg-[#FDECEE] px-8 py-2 font-semibold text-[#BE1F2E] disabled:opacity-60"
                    >
                      <BsStopFill />
                      Stop
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleCancel}
                      disabled={busy}
                      className="rounded-full border border-gray-400 bg-white px-6 py-2 font-semibold text-gray-700 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    {visibilitySelect}
                    <button
                      onClick={handleGoLive}
                      disabled={busy || !cameraStream}
                      className="rounded-full bg-gradient-to-r from-[#08AEEA] to-[#1DE78B] px-12 py-2 font-semibold text-white disabled:opacity-60"
                    >
                      {busy ? "Starting..." : "Go Live"}
                    </button>
                  </div>
                )}
              </div>
          </div>
          </div>

          {endedLive && !live && (
            <LiveSummary
              streamId={endedLive.streamId}
              streamType="donation"
              startedAt={endedLive.startedAt}
              endedAt={endedLive.endedAt}
            />
          )}

          {live && (
            <div className="mt-4 flex gap-2">
              <TabButton active={tab === "setup"} onClick={() => setTab("setup")}>
                Stream Setup
              </TabButton>
              <TabButton active={tab === "history"} onClick={() => setTab("history")}>
                Donations / Gift History
              </TabButton>
            </div>
          )}

          {live && tab === "history" ? (
            <div className="mt-4 rounded-xl bg-[#EBF8ED]">
              <DonationHistory donations={history} totalAmount={historyTotal.toFixed(2)} />
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              {!live && <h2 className="text-lg font-semibold">Stream Setup</h2>}
              <div>
                <label className="mb-2 block text-sm font-medium">Stream Title</label>
                <input
                  type="text"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={handleTitleBlur}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  maxLength={255}
                  placeholder="Enter stream title"
                  className="h-12 w-full rounded-xl border border-[#095228] px-4 focus:outline-none"
                />
              </div>
              <ModeratorsSection
                moderators={setup.moderators}
                excludeIds={moderatorIds}
                onAdd={setup.addModerator}
                onPermissionChange={setup.updateModeratorPermission}
                onRemove={setup.removeModerator}
              />
              {setup.settings.guests_enabled && (
                <MultiHostSection
                  cohosts={setup.cohosts}
                  excludeIds={hostIds}
                  onAdd={setup.addCohost}
                  onRemove={setup.removeGuest}
                />
              )}
              <PrivacyControls settings={setup.settings} onChange={setup.updateSettings} />
              <GuestPrivacy settings={setup.settings} onChange={setup.updateSettings} />
              <GiftingControls settings={setup.settings} onChange={setup.updateSettings} live={!!live} />
              <div className="space-y-8 px-2 pt-4">
                <FundInformation fund={fund} onEdit={() => setEditing(true)} />
                <BankingInformation bank={fund} onEdit={() => setEditing(true)} />
              </div>
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-4 lg:w-[300px]">
          {live && (
            <GuestRequests
              requests={setup.requests}
              guests={setup.guests}
              onReview={setup.reviewRequest}
              onRemove={setup.removeGuest}
            />
          )}
          <LiveCommentsPanel
            comments={withDonationComments(comments, donationEvents)}
            chatEnabled={!!live && chatEnabled}
            onSend={sendComment}
            disabledPlaceholder={live ? "Comments are turned off" : "Comments open when you go live"}
            getActions={getCommentActions}
            likes={likes}
            onLike={live ? like : undefined}
            className="h-[400px] w-full rounded-xl bg-[#F9FAFB] p-4 lg:h-[470px]"
          />
        </div>
      </div>

      <GoLiveDonationModal
        isOpen={editing}
        onClose={() => setEditing(false)}
        logo={logo}
        closeIcon={closeIcon}
        categories={categories}
        initialData={fund}
        onSave={handleFundSave}
      />
      <UpgradePlanModal reason={upgradeReason} onClose={() => setUpgradeReason(null)} />
    </PageLayout>
  );
};

const DonationViewer = () => {
  const { streamId } = useParams();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [donateOpen, setDonateOpen] = useState(false);
  const [stageMedia, setStageMedia] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const onStage = !!stageMedia;
  // A payment may still be confirming in the donate modal, so leaving waits until it is closed.
  // On stage the live comes from the stage itself, so the delayed player is stopped.
  const { stream, status, statusMessage, viewerCount, chatEnabled, socket } = useLiveViewer(streamId, "donation", videoRef, {
    holdRedirect: donateOpen,
    playback: !onStage,
  });
  const guestSeat = useGuestSeat(streamId, stream, socket);
  const stage = useLiveStage(streamId, stageMedia, onStage);
  const [reportOpen, setReportOpen] = useState(false);
  const [bank, setBank] = useState(null);
  const [reportReasons, setReportReasons] = useState([]);
  const [moderatorPermissions, setModeratorPermissions] = useState(null);

  const now = useNow();
  const { comments, sendComment } = useLiveComments(stream && streamId, socket);
  const donationEvents = useDonationEvents(socket);
  const { likes, like } = useLiveLikes(stream && streamId, socket);
  const getCommentActions = useCommentModeration(streamId, moderatorPermissions, stream?.user?.id);
  const isHost = !!stream && Number(stream.user?.id) === Number(getUser()?.id);
  const isLive = status === "live";
  const closeDonate = useCallback(() => setDonateOpen(false), []);
  const stageOpen = isLive || status === "paused";

  useEffect(() => () => stageMedia?.getTracks().forEach((track) => track.stop()), [stageMedia]);

  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    stageMedia?.getAudioTracks().forEach((track) => (track.enabled = micOn));
    stageMedia?.getVideoTracks().forEach((track) => (track.enabled = camOn));
  }, [stageMedia, micOn, camOn]);

  // Removed, rejected, guests turned off or the live is over: back to watching
  useEffect(() => {
    if (onStage && (guestSeat.seat !== "approved" || !stageOpen || !guestSeat.settings.guests_enabled)) setStageMedia(null);
  }, [onStage, guestSeat.seat, stageOpen, guestSeat.settings.guests_enabled]);

  useEffect(() => {
    if (!stage.error) return;
    toast.error(stage.error);
    setStageMedia(null);
  }, [stage.error]);

  const handleJoinStage = async () => {
    try {
      setMicOn(true);
      setCamOn(true);
      const media = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: "user" },
        audio: true,
      });
      // The permission prompt can outlive the page
      if (!mountedRef.current) {
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      setStageMedia(media);
    } catch (error) {
      toast.error("Allow camera and microphone access to join the stage");
    }
  };

  const handleLeaveStage = async () => {
    await guestSeat.leaveStage();
    setStageMedia(null);
  };

  useEffect(() => {
    if (!stream?.is_moderator) return;
    const me = Number(getUser()?.id);
    axios
      .get(`${API_BASE_URL}/live/${streamId}/moderators`, { headers: authHeaders() })
      .then((res) => {
        const own = (res.data.result?.moderators || []).find((m) => Number(m.moderator_id) === me);
        setModeratorPermissions(own?.permissions || null);
      })
      .catch(() => setModeratorPermissions(null));
  }, [stream, streamId]);

  // The host can add, change or remove moderators during the live
  useEffect(() => {
    if (!socket) return;
    const me = Number(getUser()?.id);
    const onAdded = (data) => Number(data.moderator?.id) === me && setModeratorPermissions(data.permissions);
    const onUpdated = (data) => Number(data.moderator_id) === me && setModeratorPermissions(data.permissions);
    const onRemoved = (data) => Number(data.moderator_id) === me && setModeratorPermissions(null);
    socket.on("moderator-added", onAdded);
    socket.on("moderator-updated", onUpdated);
    socket.on("moderator-removed", onRemoved);
    return () => {
      socket.off("moderator-added", onAdded);
      socket.off("moderator-updated", onUpdated);
      socket.off("moderator-removed", onRemoved);
    };
  }, [socket]);

  useEffect(() => {
    if (!stream || isHost) return;
    axios
      .get(`${API_BASE_URL}/live/${streamId}/safety-options`, { headers: authHeaders() })
      .then((res) => setReportReasons(res.data.result?.report_categories || []))
      .catch(() => setReportReasons([]));
  }, [stream, streamId, isHost]);

  // The API shares the fundraiser's masked bank details only while the live is on and donations are open,
  // so they are loaded when the live (re)starts rather than only when the page opens
  useEffect(() => {
    if (!stream || isHost || bank || status !== "live" || !stream.donation_enabled) return;
    axios
      .get(`${API_BASE_URL}/live/${streamId}/donation/bottom-sheet`, { headers: authHeaders() })
      .then((res) => setBank(res.data.result?.bank_info || null))
      .catch(() => setBank(null));
  }, [stream, streamId, isHost, status, bank]);

  const handleReport = async ({ reason, description }) => {
    try {
      await axios.post(
        `${API_BASE_URL}/live/${streamId}/reports`,
        { reported_user_id: stream.user.id, reason_code: reason, ...(description && { description }) },
        { headers: authHeaders() }
      );
      toast.success("Thanks, your report has been submitted");
    } catch (error) {
      throw new Error(error.response?.data?.message || "Unable to submit the report");
    }
  };

  return (
    <PageLayout>
      {stream && (
        <LiveHeader
          title={stream.title}
          startedAt={isLive || status === "paused" ? stream.started_at : null}
          visibility={
            stream.visibility === "private" || (stream.view_audience && stream.view_audience !== "public") ? "private" : "public"
          }
          now={now}
        />
      )}

      <div className="mt-4 flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          <div className="rounded-xl bg-[#EBF8ED]">
            <div className="relative h-[260px] overflow-hidden rounded-xl bg-black text-white sm:h-[400px]">
              {onStage ? (
                <StageTiles
                  layout={stageLayout(guestSeat.settings.layout, stage.participants.filter((p) => p.role !== "host").length + 1)}
                  tiles={[
                    {
                      key: "host",
                      name: personName(stream.user),
                      content: (
                        <>
                          {stage.participants.some((p) => p.role === "host") ? (
                            <StageVideo stream={stage.participants.find((p) => p.role === "host").mediaStream} />
                          ) : (
                            <div className="flex h-full items-center justify-center text-sm">Connecting to the host...</div>
                          )}
                          {status === "paused" && <PausedNotice subtitle="The Host Will Resume Shortly" />}
                        </>
                      ),
                    },
                    {
                      key: "me",
                      name: personName(getUser()),
                      content: <StageVideo stream={stageMedia} muted mirrored />,
                      action: <RemoveFromStageButton onClick={handleLeaveStage} title="Leave stage" />,
                    },
                    ...stage.participants
                      .filter((p) => p.role !== "host")
                      .map((p) => ({ key: p.id, name: p.username, content: <StageVideo stream={p.mediaStream} /> })),
                  ]}
                />
              ) : (
                <video ref={videoRef} autoPlay playsInline muted={muted} className="h-full w-full object-cover" />
              )}
              {/* While paused the host's guests stay on screen, so only a banner is shown */}
              {statusMessage &&
                !onStage &&
                (status === "paused" ? (
                  <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/70 px-5 py-2 text-sm font-semibold">
                    {statusMessage}
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/70 px-6 text-center text-lg font-semibold">
                    {statusMessage}
                  </div>
                ))}
              {stream && !onStage && <NameBadge name={personName(stream.user)} picture={stream.user?.profile_picture_url} />}
              {stream && !onStage && (
                <>
                  <button
                    onClick={() => copyLiveLink(streamId)}
                    title="Copy live link"
                    className="absolute right-3 top-3 rounded-full bg-black/50 p-2"
                  >
                    <img src={shareIcon} alt="Share" className="h-4 w-4" />
                  </button>
                  <span className="absolute bottom-4 left-4 text-sm font-semibold">{viewerCount} Viewers</span>
                </>
              )}
              {isLive && (
                <span
                  className={`absolute rounded-full bg-[#BE1F2E] px-4 py-1 text-xs font-semibold ${
                    onStage ? "right-16 top-3 z-10" : "bottom-4 right-4"
                  }`}
                >
                  LIVE
                </span>
              )}
            </div>

            {onStage ? (
              <div className="flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-3">
                  <ToggleButton
                    on={micOn}
                    onClick={() => setMicOn((value) => !value)}
                    title={micOn ? "Mute microphone" : "Unmute microphone"}
                    onIcon={<BsMicFill />}
                    offIcon={<BsMicMuteFill />}
                  />
                  <ToggleButton
                    on={camOn}
                    onClick={() => setCamOn((value) => !value)}
                    title={camOn ? "Turn camera off" : "Turn camera on"}
                    onIcon={<BsCameraVideoFill />}
                    offIcon={<BsCameraVideoOffFill />}
                  />
                  <button
                    onClick={() => setReportOpen(true)}
                    className="flex items-center gap-1 rounded-full border border-[#BE1F2E] px-4 py-1 text-sm text-[#BE1F2E]"
                  >
                    <FaExclamationTriangle />
                    Report
                  </button>
                  {stream.started_at && (
                    <span className="text-lg font-semibold tabular-nums text-[#98911F]">
                      {formatElapsed(stream.started_at, now)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setDonateOpen(true)}
                    disabled={!isLive || !stream.donation_enabled}
                    className="flex items-center gap-2 rounded-full bg-[#095228] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    <FaHeart />
                    Donate / Gift
                  </button>
                  <LiveLinkButtons streamId={streamId} />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-3">
                  <ToggleButton
                    on={!muted}
                    onClick={() => setMuted((value) => !value)}
                    title={muted ? "Unmute" : "Mute"}
                    onIcon={<BsVolumeUpFill />}
                    offIcon={<BsVolumeMuteFill />}
                  />
                  {moderatorPermissions && (
                    <span className="rounded-full bg-[#095228] px-3 py-1 text-xs font-semibold text-white">Moderator</span>
                  )}
                  {stream && !isHost && (
                    <button
                      onClick={() => setReportOpen(true)}
                      className="flex items-center gap-1 rounded-full border border-[#BE1F2E] px-4 py-1 text-sm text-[#BE1F2E]"
                    >
                      <FaExclamationTriangle />
                      Report
                    </button>
                  )}
                  {stream && !isHost && stageOpen && guestSeat.settings.guests_enabled && (
                    <>
                      {guestSeat.seat === "approved" && (
                        <button
                          onClick={handleJoinStage}
                          className="rounded-full bg-[#095228] px-4 py-1 text-sm font-semibold text-white"
                        >
                          Join Stage
                        </button>
                      )}
                      {guestSeat.seat === "invited" && (
                        <>
                          <button
                            onClick={() => guestSeat.respondToInvite("accept")}
                            className="rounded-full bg-[#095228] px-4 py-1 text-sm font-semibold text-white"
                          >
                            Accept Invite
                          </button>
                          <button
                            onClick={() => guestSeat.respondToInvite("decline")}
                            className="rounded-full border border-gray-400 px-4 py-1 text-sm"
                          >
                            Decline
                          </button>
                        </>
                      )}
                      {(!guestSeat.seat || guestSeat.seat === "requested") && (
                        <button
                          onClick={guestSeat.requestToJoin}
                          disabled={guestSeat.seat === "requested"}
                          className="rounded-full border border-[#095228] px-4 py-1 text-sm font-semibold text-[#095228] disabled:opacity-60"
                        >
                          {guestSeat.seat === "requested" ? "Request Sent" : "Request to Join"}
                        </button>
                      )}
                    </>
                  )}
                </div>
                {stream && !isHost && (
                  <div className="text-right">
                    <button
                      onClick={() => setDonateOpen(true)}
                      disabled={!isLive || !stream.donation_enabled}
                      className="ml-auto flex items-center gap-2 rounded-full bg-[#095228] px-6 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      <FaHeart />
                      Donate / Gift
                    </button>
                    <p className="mt-1 text-[10px]">
                      {stream.donation_enabled
                        ? "*Viewers can send real-time donations during this live stream."
                        : "*Donations are turned off for this live stream."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {stream && (
            <div className="mt-6 space-y-8 px-2">
              <FundInformation
                fund={{
                  title: stream.title,
                  description: stream.description,
                  category: stream.category,
                  goalAmount: stream.goal_amount,
                }}
              />
              {bank && (
                <BankingInformation
                  bank={{
                    beneficiaryName: bank.account_holder_name || bank.organization_name,
                    bankName: bank.bank_name,
                    accountNumber: bank.bank_account_last4 && `**** ${bank.bank_account_last4}`,
                    ifsc: bank.bank_ifsc_code,
                  }}
                />
              )}
            </div>
          )}
        </div>

        <LiveCommentsPanel
          comments={withDonationComments(comments, donationEvents)}
          chatEnabled={chatEnabled && isLive}
          onSend={sendComment}
          getActions={getCommentActions}
          likes={likes}
          onLike={isLive && stream?.reactions_enabled ? like : undefined}
          className="h-[400px] w-full rounded-xl bg-[#F9FAFB] p-4 lg:h-[470px] lg:w-[300px]"
        />
      </div>

      <GoLiveDonationgiftModal isOpen={donateOpen} onClose={closeDonate} logo={logo} closeIcon={closeIcon} stream={stream} />
      <ReportStreamModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        onSubmit={handleReport}
        logo={logo}
        closeIcon={closeIcon}
        reasons={reportReasons}
      />
    </PageLayout>
  );
};

// Keyed by the live so opening another live (e.g. from a notification) starts with fresh state
const GoliveDonation = ({ viewerMode = false }) => {
  const { streamId } = useParams();
  return viewerMode ? <DonationViewer key={streamId} /> : <HostStudio />;
};

export default GoliveDonation;
